import { useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import React, { useEffect, useMemo } from 'react';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
} from '~/atoms/dashboardAtom';
import { GET_PROPOSED_PORTFOLIO_STATS } from '~/graphql/queries/portfolio';
import { ProposedPortfolioWidget } from './ProposedPortfolioWidget';
import { getMetricDescriptionWithFormula } from '~/utils/metricsMapping';

interface AnalysisData {
  risk_metrics: {
    beta_vs_sp500: number;
    max_drawdown: number;
    var_30d_95: number;
    sharpe_ratio: number;
    volatility: number;
    sortino_ratio?: number;
    [key: string]: number;
  };
  stress_tests?: {
    inflation_shock: number | string;
    market_crash_10pct: number | string;
    market_crash_20pct: number | string;
    geopolitical_crisis: number | string;
    interest_rate_shock: number | string;
    [key: string]: number | string;
  };
  correlation_analysis?: {
    avg_correlation: number | string;
    max_correlation: number | string;
    min_correlation: number | string;
    asset_correlations: {
      [asset: string]: {
        [otherAsset: string]: number | string;
      };
    };
  };
  risk_comparison?: {
    current: {
      var_30d_95: number | string;
      max_drawdown: number | string;
      sharpe_ratio: number | string;
      sortino_ratio?: number | string;
      fx_volatility?: number | string;
      expected_volatility?: number | string;
      volatility?: number | string;
      beta?: number | string;
      beta_ex_ante?: number | string;
      beta_ex_post?: number | string;
      tracking_error?: number | string;
      tracking_error_ex_ante?: number | string;
      tracking_error_ex_post?: number | string;
      ex_ante_cvar_pct?: number | string;
      forward_max_drawdown_pct?: number | string;
      shortfall_probability_pct?: number | string;
      drawdown_budget_utilization_pct?: number | string;
      drawdown_budget_pct?: number | string;
      [key: string]: number | string;
    };
    projected?: {
      var_30d_95: number | string;
      max_drawdown: number | string;
      sharpe_ratio: number | string;
      sortino_ratio?: number | string;
      fx_volatility?: number | string;
      expected_volatility?: number | string;
      volatility?: number | string;
      beta?: number | string;
      beta_ex_ante?: number | string;
      beta_ex_post?: number | string;
      tracking_error?: number | string;
      tracking_error_ex_ante?: number | string;
      tracking_error_ex_post?: number | string;
      ex_ante_cvar_pct?: number | string;
      forward_max_drawdown_pct?: number | string;
      shortfall_probability_pct?: number | string;
      drawdown_budget_utilization_pct?: number | string;
      drawdown_budget_pct?: number | string;
      [key: string]: number | string;
    };
    proposed?: {
      var_30d_95: number | string;
      max_drawdown: number | string;
      sharpe_ratio: number | string;
      sortino_ratio?: number | string;
      fx_volatility?: number | string;
      expected_volatility?: number | string;
      volatility?: number | string;
      beta?: number | string;
      beta_ex_ante?: number | string;
      beta_ex_post?: number | string;
      tracking_error?: number | string;
      tracking_error_ex_ante?: number | string;
      tracking_error_ex_post?: number | string;
      ex_ante_cvar_pct?: number | string;
      forward_max_drawdown_pct?: number | string;
      shortfall_probability_pct?: number | string;
      drawdown_budget_utilization_pct?: number | string;
      drawdown_budget_pct?: number | string;
      [key: string]: number | string;
    };
  };
  current?: {
    beta?: number | string;
    tracking_error?: number | string;
    ex_ante_cvar_pct?: number | string;
    shortfall_probability_pct?: number | string;
    forward_max_drawdown_pct?: number | string;
    drawdown_budget_utilization_pct?: number | string;
    drawdown_budget_pct?: number | string;
    [key: string]: number | string;
  };
  proposed?: {
    beta?: number | string;
    tracking_error?: number | string;
    ex_ante_cvar_pct?: number | string;
    shortfall_probability_pct?: number | string;
    forward_max_drawdown_pct?: number | string;
    drawdown_budget_utilization_pct?: number | string;
    drawdown_budget_pct?: number | string;
    [key: string]: number | string;
  };
  allocation_breakdown: {
    [key: string]: number;
  };
}

interface ProposedPortfolioData {
  EQPortfoliosAnalysis: {
    var_30d_95: number;
    sharpe_ratio: number;
    analysis_data: AnalysisData;
  }[];
}

export interface ProposedPortfolioWidgetWrapperProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
}

export const ProposedPortfolioWidgetWrapper: React.FC<
  ProposedPortfolioWidgetWrapperProps
> = ({ widgetId, handleRemoveWidget, shortDescription }) => {
  // Get the latest snapshot ID from the atom
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);

  // Use the latest snapshot ID or fallback to the hardcoded one
  const snapshotId = latestSnapshotId;

  const { data, loading, error, refetch } = useQuery<ProposedPortfolioData>(
    GET_PROPOSED_PORTFOLIO_STATS,
    {
      variables: { snapshotId },
      notifyOnNetworkStatusChange: true,
      skip: !snapshotId, // Skip query if no snapshot ID is available
    },
  );

  useEffect(() => {
    if (snapshotId) {
      refetch();
    }
  }, [snapshotId, refetch]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      refetch();
    }
  }, [triggerHardRefetch, refetch]);

  const portfolioData = useMemo(() => {
    if (loading || !data) return [];

    const analysis = data.EQPortfoliosAnalysis?.[0];
    const transformedData = [];

    if (analysis) {
      // Only show Max Drawdown from risk metrics since VaR, Sharpe, and Volatility are shown elsewhere
      if (analysis.analysis_data?.risk_comparison) {
        const { current, projected, proposed } =
          analysis.analysis_data.risk_comparison;
        const proposedData = proposed || projected;

        // Max Drawdown
        if (
          current?.max_drawdown !== undefined &&
          proposedData?.max_drawdown !== undefined
        ) {
          const currentVal = parseFloat(current.max_drawdown);
          const proposedVal = parseFloat(proposedData.max_drawdown);
          const change = proposedVal - currentVal;
          const maxDrawdownLabel = 'Max Drawdown';
          transformedData.push({
            label: maxDrawdownLabel,
            description: getMetricDescriptionWithFormula(maxDrawdownLabel),
            currentValue: `${currentVal.toFixed(2)}%`,
            proposedValue: `${proposedVal.toFixed(2)}%`,
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(2)}%`,
            value: `${proposedVal.toFixed(2)}%`,
            isPositive: change > 0, // Less negative drawdown is better
          });
        }

        // Sortino Ratio
        if (
          current?.sortino_ratio !== undefined &&
          proposedData?.sortino_ratio !== undefined
        ) {
          const currentVal = parseFloat(current.sortino_ratio);
          const proposedVal = parseFloat(proposedData.sortino_ratio);
          const change = proposedVal - currentVal;
          const sortinoLabel = 'Sortino Ratio';
          transformedData.push({
            label: sortinoLabel,
            description: getMetricDescriptionWithFormula(sortinoLabel),
            currentValue: currentVal.toFixed(2),
            proposedValue: proposedVal.toFixed(2),
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(2)}`,
            value: proposedVal.toFixed(2),
            isPositive: change > 0, // Higher Sortino is better
          });
        }

        // FX Volatility
        if (
          current?.fx_volatility !== undefined &&
          proposedData?.fx_volatility !== undefined
        ) {
          const currentVal = parseFloat(current.fx_volatility);
          const proposedVal = parseFloat(proposedData.fx_volatility);
          const change = proposedVal - currentVal;
          const fxVolLabel = 'FX Volatility';
          transformedData.push({
            label: fxVolLabel,
            description: getMetricDescriptionWithFormula(fxVolLabel),
            currentValue: `${currentVal.toFixed(1)}%`,
            proposedValue: `${proposedVal.toFixed(1)}%`,
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
            value: `${proposedVal.toFixed(1)}%`,
            isPositive: change < 0, // Lower FX volatility is better
          });
        }

        // Beta (Combined Ex-Ante and Ex-Post)
        if (
          current?.beta_ex_ante !== undefined &&
          proposedData?.beta_ex_ante !== undefined &&
          current?.beta_ex_post !== undefined &&
          proposedData?.beta_ex_post !== undefined
        ) {
          const anteCurrentVal = parseFloat(current.beta_ex_ante);
          const anteProposedVal = parseFloat(proposedData.beta_ex_ante);
          const postCurrentVal = parseFloat(current.beta_ex_post);
          const postProposedVal = parseFloat(proposedData.beta_ex_post);

          const betaLabel = 'Beta';
          transformedData.push({
            label: betaLabel,
            description: getMetricDescriptionWithFormula(betaLabel),
            value: '', // Not used for this format
            exAnteCurrentValue: anteCurrentVal.toFixed(2),
            exAnteProposedValue: anteProposedVal.toFixed(2),
            exPostCurrentValue: postCurrentVal.toFixed(2),
            exPostProposedValue: postProposedVal.toFixed(2),
          });
        }

        // Tracking Error (Combined Ex-Ante and Ex-Post)
        if (
          current?.tracking_error_ex_ante !== undefined &&
          proposedData?.tracking_error_ex_ante !== undefined &&
          current?.tracking_error_ex_post !== undefined &&
          proposedData?.tracking_error_ex_post !== undefined
        ) {
          const anteCurrentVal = parseFloat(current.tracking_error_ex_ante);
          const anteProposedVal = parseFloat(
            proposedData.tracking_error_ex_ante,
          );
          const postCurrentVal = parseFloat(current.tracking_error_ex_post);
          const postProposedVal = parseFloat(
            proposedData.tracking_error_ex_post,
          );

          const trackingErrorLabel = 'Tracking Error';
          transformedData.push({
            label: trackingErrorLabel,
            description: getMetricDescriptionWithFormula(trackingErrorLabel),
            value: '', // Not used for this format
            exAnteCurrentValue: `${anteCurrentVal.toFixed(1)}%`,
            exAnteProposedValue: `${anteProposedVal.toFixed(1)}%`,
            exPostCurrentValue: `${postCurrentVal.toFixed(1)}%`,
            exPostProposedValue: `${postProposedVal.toFixed(1)}%`,
          });
        }

        // Forward Max Drawdown from risk_comparison
        if (
          current?.forward_max_drawdown_pct !== undefined &&
          proposedData?.forward_max_drawdown_pct !== undefined
        ) {
          const currentVal = parseFloat(current.forward_max_drawdown_pct);
          const proposedVal = parseFloat(proposedData.forward_max_drawdown_pct);
          const change = proposedVal - currentVal;
          const forwardDDLabel = 'Forward Max Drawdown';
          transformedData.push({
            label: forwardDDLabel,
            description: getMetricDescriptionWithFormula(forwardDDLabel),
            currentValue: `${currentVal.toFixed(1)}%`,
            proposedValue: `${proposedVal.toFixed(1)}%`,
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
            value: `${proposedVal.toFixed(1)}%`,
            isPositive: change < 0,
          });
        }

        // Shortfall Probability from risk_comparison
        if (
          current?.shortfall_probability_pct !== undefined &&
          proposedData?.shortfall_probability_pct !== undefined
        ) {
          const currentVal = parseFloat(current.shortfall_probability_pct);
          const proposedVal = parseFloat(
            proposedData.shortfall_probability_pct,
          );
          const change = proposedVal - currentVal;
          const shortfallLabel = 'Shortfall Probability (vs 0%)';
          transformedData.push({
            label: shortfallLabel,
            description: getMetricDescriptionWithFormula(shortfallLabel),
            currentValue: `${currentVal.toFixed(1)}%`,
            proposedValue: `${proposedVal.toFixed(1)}%`,
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
            value: `${proposedVal.toFixed(1)}%`,
            isPositive: change < 0,
          });
        }

        // Drawdown Budget Utilization from risk_comparison
        if (
          current?.drawdown_budget_utilization_pct !== undefined &&
          proposedData?.drawdown_budget_utilization_pct !== undefined
        ) {
          const currentVal = parseFloat(
            current.drawdown_budget_utilization_pct,
          );
          const proposedVal = parseFloat(
            proposedData.drawdown_budget_utilization_pct,
          );
          const change = proposedVal - currentVal;
          const budgetUtilLabel = 'Drawdown Budget Utilization';
          transformedData.push({
            label: budgetUtilLabel,
            description: getMetricDescriptionWithFormula(budgetUtilLabel),
            currentValue: `${currentVal.toFixed(1)}%`,
            proposedValue: `${proposedVal.toFixed(1)}%`,
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
            value: `${proposedVal.toFixed(1)}%`,
            isPositive: change <= 0,
          });
        }

        // Drawdown Budget (Total) from risk_comparison
        if (
          current?.drawdown_budget_pct !== undefined &&
          proposedData?.drawdown_budget_pct !== undefined
        ) {
          const currentVal = parseFloat(current.drawdown_budget_pct);
          const proposedVal = parseFloat(proposedData.drawdown_budget_pct);
          const change = proposedVal - currentVal;
          const budgetLabel = 'Drawdown Budget';
          transformedData.push({
            label: budgetLabel,
            description: getMetricDescriptionWithFormula(budgetLabel),
            currentValue: `${currentVal.toFixed(1)}%`,
            proposedValue: `${proposedVal.toFixed(1)}%`,
            changeValue:
              change !== 0
                ? `${change > 0 ? '+' : ''}${change.toFixed(1)}%`
                : '0.0%',
            value: `${proposedVal.toFixed(1)}%`,
            isPositive: undefined,
          });
        }
      } else {
        // Fallback - only show Max Drawdown
        if (analysis.analysis_data?.risk_metrics?.max_drawdown !== undefined) {
          const maxDrawdown = analysis.analysis_data.risk_metrics.max_drawdown;
          const maxDrawdownNum =
            typeof maxDrawdown === 'number'
              ? maxDrawdown
              : parseFloat(maxDrawdown) || 0;
          const maxDrawdownLabel = 'Max Drawdown';
          transformedData.push({
            label: maxDrawdownLabel,
            description: getMetricDescriptionWithFormula(maxDrawdownLabel),
            value: `${maxDrawdownNum.toFixed(2)}%`,
          });
        }
      }

      // Always check for these metrics in top-level current/proposed
      if (analysis.analysis_data?.current && analysis.analysis_data?.proposed) {
        const current = analysis.analysis_data.current;
        const proposed = analysis.analysis_data.proposed;

        // Shortfall Probability
        if (
          current.shortfall_probability_pct !== undefined &&
          proposed.shortfall_probability_pct !== undefined
        ) {
          const currentVal = parseFloat(current.shortfall_probability_pct);
          const proposedVal = parseFloat(proposed.shortfall_probability_pct);
          const change = proposedVal - currentVal;
          const shortfallLabel = 'Shortfall Probability (vs 0%)';
          transformedData.push({
            label: shortfallLabel,
            description: getMetricDescriptionWithFormula(shortfallLabel),
            currentValue: `${currentVal.toFixed(1)}%`,
            proposedValue: `${proposedVal.toFixed(1)}%`,
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
            value: `${proposedVal.toFixed(1)}%`,
            isPositive: change < 0,
          });
        }

        // Forward Max Drawdown
        if (
          current.forward_max_drawdown_pct !== undefined &&
          proposed.forward_max_drawdown_pct !== undefined
        ) {
          const currentVal = parseFloat(current.forward_max_drawdown_pct);
          const proposedVal = parseFloat(proposed.forward_max_drawdown_pct);
          const change = proposedVal - currentVal;
          const forwardDDLabel = 'Forward Max Drawdown';
          transformedData.push({
            label: forwardDDLabel,
            description: getMetricDescriptionWithFormula(forwardDDLabel),
            currentValue: `${currentVal.toFixed(1)}%`,
            proposedValue: `${proposedVal.toFixed(1)}%`,
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
            value: `${proposedVal.toFixed(1)}%`,
            isPositive: change < 0,
          });
        }

        // Drawdown Budget Utilization
        if (
          current.drawdown_budget_utilization_pct !== undefined &&
          proposed.drawdown_budget_utilization_pct !== undefined
        ) {
          const currentVal = parseFloat(
            current.drawdown_budget_utilization_pct,
          );
          const proposedVal = parseFloat(
            proposed.drawdown_budget_utilization_pct,
          );
          const change = proposedVal - currentVal;
          const budgetUtilLabel = 'Drawdown Budget Utilization';
          transformedData.push({
            label: budgetUtilLabel,
            description: getMetricDescriptionWithFormula(budgetUtilLabel),
            currentValue: `${currentVal.toFixed(1)}%`,
            proposedValue: `${proposedVal.toFixed(1)}%`,
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
            value: `${proposedVal.toFixed(1)}%`,
            isPositive: change <= 0,
          });
        }

        // Drawdown Budget (Total)
        if (
          current.drawdown_budget_pct !== undefined &&
          proposed.drawdown_budget_pct !== undefined
        ) {
          const currentVal = parseFloat(current.drawdown_budget_pct);
          const proposedVal = parseFloat(proposed.drawdown_budget_pct);
          const change = proposedVal - currentVal;
          const budgetLabel = 'Drawdown Budget';
          transformedData.push({
            label: budgetLabel,
            description: getMetricDescriptionWithFormula(budgetLabel),
            currentValue: `${currentVal.toFixed(1)}%`,
            proposedValue: `${proposedVal.toFixed(1)}%`,
            changeValue:
              change !== 0
                ? `${change > 0 ? '+' : ''}${change.toFixed(1)}%`
                : '0.0%',
            value: `${proposedVal.toFixed(1)}%`,
            isPositive: undefined,
          });
        }
      }

      // Add allocation breakdown data
      if (analysis.analysis_data?.allocation_breakdown) {
        const breakdown = analysis.analysis_data.allocation_breakdown;

        // Handle cash allocation (current vs proposed)
        if (
          breakdown.current_cash_allocation !== undefined &&
          breakdown.cash_allocation !== undefined
        ) {
          const currentCash = parseFloat(breakdown.current_cash_allocation);
          const proposedCash = parseFloat(breakdown.cash_allocation);
          const change = proposedCash - currentCash;
          const cashAllocLabel = 'Cash Allocation';
          transformedData.push({
            label: cashAllocLabel,
            description: getMetricDescriptionWithFormula(cashAllocLabel),
            currentValue: `${currentCash.toFixed(1)}%`,
            proposedValue: `${proposedCash.toFixed(1)}%`,
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
            value: `${proposedCash.toFixed(1)}%`,
            isPositive: undefined, // Neutral - neither good nor bad
          });
        } else if (breakdown.cash_allocation !== undefined) {
          // Only proposed cash allocation available
          const proposedCash = parseFloat(breakdown.cash_allocation);
          const cashAllocLabel = 'Cash Allocation';
          transformedData.push({
            label: cashAllocLabel,
            description: getMetricDescriptionWithFormula(cashAllocLabel),
            value: `${proposedCash.toFixed(1)}%`,
          });
        }

        // Handle total allocation (current vs proposed)
        if (
          breakdown.current_total_allocation !== undefined &&
          breakdown.total_allocation !== undefined
        ) {
          const currentTotal = parseFloat(breakdown.current_total_allocation);
          const proposedTotal = parseFloat(breakdown.total_allocation);
          const change = proposedTotal - currentTotal;
          const totalAllocLabel = 'Total Allocation';
          transformedData.push({
            label: totalAllocLabel,
            description: getMetricDescriptionWithFormula(totalAllocLabel),
            currentValue: `${currentTotal.toFixed(1)}%`,
            proposedValue: `${proposedTotal.toFixed(1)}%`,
            changeValue: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
            value: `${proposedTotal.toFixed(1)}%`,
            isPositive: undefined, // Neutral
          });
        } else if (breakdown.total_allocation !== undefined) {
          // Only proposed total allocation available
          const proposedTotal = parseFloat(breakdown.total_allocation);
          const totalAllocLabel = 'Total Allocation';
          transformedData.push({
            label: totalAllocLabel,
            description: getMetricDescriptionWithFormula(totalAllocLabel),
            value: `${proposedTotal.toFixed(1)}%`,
          });
        }

        // Sector percentages removed per user request
        // Handle Allocation Analysiss (current vs proposed)
        // if (breakdown.current_sectors && breakdown.proposed_sectors) {
        //   Object.entries(breakdown.proposed_sectors).forEach(
        //     ([sector, proposedValue]) => {
        //       const currentValue = breakdown.current_sectors[sector];
        //       if (currentValue !== undefined) {
        //         const currentVal = parseFloat(currentValue);
        //         const proposedVal = parseFloat(proposedValue);
        //         const change = proposedVal - currentVal;
        //         transformedData.push({
        //           label: `${sector} Allocation`,
        //           currentValue: `${currentVal.toFixed(1)}%`,
        //           proposedValue: `${proposedVal.toFixed(1)}%`,
        //           changeValue: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
        //           value: `${proposedVal.toFixed(1)}%`,
        //           isPositive: undefined, // Allocation Analysis changes are neutral
        //         });
        //       }
        //     },
        //   );
        // }

        // Handle other allocation fields (these are proposed values without current comparison)
        Object.entries(breakdown).forEach(([key, value]) => {
          if (
            key.includes('allocation') &&
            !key.includes('current_') &&
            !key.includes('total_') &&
            !key.includes('cash_') &&
            (typeof value === 'number' || typeof value === 'string') &&
            (typeof value === 'number' ? value > 0 : parseFloat(value) > 0)
          ) {
            const label = key
              .replace(/_allocation$/, '')
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            const numericValue =
              typeof value === 'number' ? value : parseFloat(value) || 0;

            transformedData.push({
              label: `${label} Allocation`,
              value: `${numericValue.toFixed(1)}%`,
            });
          }
        });
      }

      // COMMENTED OUT: Add stress test results at the bottom
      // if (analysis.analysis_data?.stress_tests) {
      //   const stressTests = analysis.analysis_data.stress_tests;
      //   const stressTestItems = [];
      //
      //   Object.entries(stressTests).forEach(([testName, result]) => {
      //     const value = parseFloat(result);
      //     if (!isNaN(value)) {
      //       // Convert snake_case test names to readable labels
      //       const label = testName
      //         .replace(/_/g, ' ')
      //         .replace(/pct/g, '%')
      //         .split(' ')
      //         .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      //         .join(' ');
      //
      //       stressTestItems.push({
      //         name: label,
      //         value: `${value > 0 ? '+' : ''}${value.toFixed(1)}%`,
      //         isPositive: value > 0,
      //       });
      //     }
      //   });
      //
      //   if (stressTestItems.length > 0) {
      //     transformedData.push({
      //       label: 'Stress Test',
      //       value: '', // Not used for stress test format
      //       stressTests: stressTestItems,
      //       isPositive: undefined,
      //     });
      //   }
      // }

      // Correlation cards moved to CorrelationClusterWidget
    }

    // Sort metrics with specific positioning
    const sortedData = transformedData.sort((a, b) => {
      // Define priority order (lower number = higher priority / shown first)
      const getPriority = (metric: typeof a) => {
        // Top row: Max Drawdown and Total Allocation
        if (metric.label === 'Max Drawdown') return 1;
        if (metric.label === 'Total Allocation') return 2;

        // Second row: Beta and Tracking Error (ex-ante/ex-post combined)
        if (metric.label === 'Beta') return 3;
        if (metric.label === 'Tracking Error') return 4;

        // Other current/proposed metrics
        if (metric.currentValue && metric.proposedValue) return 5;

        // Single value metrics
        if (
          !metric.currentValue &&
          !metric.proposedValue &&
          !metric.stressTests
        )
          return 6;

        // Bottom: Stress Test (full width)
        if (metric.label === 'Stress Test') return 99;

        return 7;
      };

      return getPriority(a) - getPriority(b);
    });

    return sortedData;
  }, [data, loading]);

  return (
    <ProposedPortfolioWidget
      data={portfolioData}
      isLoading={loading}
      error={error || null}
      refetch={refetch}
      widgetId={widgetId}
      handleRemoveWidget={handleRemoveWidget}
      title={shortDescription}
      snapshotId={snapshotId}
    />
  );
};
